import { IMPERSONATION_STORAGE_KEY, type ApiErrorBody } from '@dimmi/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

// ── Impersonation (admin "ouvrir le compte d'un client" — CLAUDE.md §6) ───
// The bearer token lives in sessionStorage, which is per-tab: only the
// impersonation tab carries it, the admin's other tabs keep their cookie
// session untouched. Requests attach it as an Authorization header.
export function getImpersonationToken(): string | null {
  try {
    return sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setImpersonationToken(token: string): void {
  try {
    sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, token);
  } catch {
    /* sessionStorage unavailable — impersonation just won't persist */
  }
}

export function clearImpersonationToken(): void {
  try {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const finalHeaders: Record<string, string> = { ...(headers as Record<string, string>) };
  let body: BodyInit | undefined = rest.body as BodyInit | undefined;

  if (json !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
    body = JSON.stringify(json);
  }

  const impToken = getImpersonationToken();
  if (impToken) finalHeaders['Authorization'] = `Bearer ${impToken}`;

  const res = await fetch(path, {
    ...rest,
    headers: finalHeaders,
    body,
    credentials: 'include',
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  const data: unknown = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const err = data as ApiErrorBody;
    if (err && typeof err === 'object' && 'error' in err) {
      throw new ApiError(res.status, err.error.code, err.error.message, err.error.details);
    }
    throw new ApiError(res.status, 'HTTP_ERROR', `Request failed (${res.status})`);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, json?: unknown) => request<T>(path, { method: 'POST', json }),
  patch: <T>(path: string, json?: unknown) => request<T>(path, { method: 'PATCH', json }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
